"use client";

import { Colors } from "@/theme";
import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { ToastType } from "@/providers/toast-provider";
import CrossSVG from "@/assets/svgs/cross.svg";
import AlertSVG from "@/assets/svgs/alert.svg";
import Image from "next/image";

/**
 * Types
 */

export interface ToastButtonProps {
  label: string;
  onClick: () => void;
}

export interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: () => void;
  buttonProps?: ToastButtonProps;
}

/**
 * Styled Components
 */

const getToastBackgroundColor = (type: ToastProps["type"]) => {
  switch (type) {
    case "success":
      return Colors.green200;
    case "error":
      return Colors.red100;
    case "info":
      return Colors.blue50;
    case "simple":
      return Colors.white;
    default:
      return Colors.yellow50;
  }
};

const getToastTextColor = (type: ToastProps["type"]) => {
  switch (type) {
    case "success":
      return Colors.green700;
    case "error":
      return Colors.blackPurple;
    case "info":
      return Colors.blue700;
    case "simple":
      return Colors.blackPurple;
    default:
      return Colors.yellow600;
  }
};

const getToastIcon = (type: ToastProps["type"]) => {
  switch (type) {
    case "error":
      return AlertSVG;
  }
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-20px); }
`;

const ToastContainer = styled.div<{
  type: ToastType;
  $isVisible: boolean;
}>`
  position: fixed;
  bottom: 20px;
  left: 20px;
  background-color: ${({ type }) => getToastBackgroundColor(type)};
  color: ${({ type }) => getToastTextColor(type)};
  padding: 16px;
  border-radius: 8px;
  min-width: 250px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  animation: ${({ $isVisible }) => ($isVisible ? fadeIn : fadeOut)} 0.5s
    ease-in-out;
  transition: opacity 0.5s ease-in-out;

  font-family: ${({ theme }) => theme.Fonts.ttNormsProNormal};
  font-size: 14px;
  font-style: normal;
  line-height: 18px;
`;

const ToastContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button<{ type: ToastType }>`
  background: none;
  border: none;
  color: ${({ type }) => getToastTextColor(type)};
  cursor: pointer;
  margin-left: 10px;
`;

const Button = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.Colors.graySmoke500};
  text-align: center;
  font-family: ${({ theme }) => theme.Fonts.ttNormsProBold};
  font-size: 12px;
  line-height: 16px;
  margin-left: 12px;
`;

const Text = styled.p`
  margin: 0;
`;

/**
 * Constants
 */

export const TOAST_DURATION = 5000;

/**
 * Toast Component
 */

const Toast: React.FC<ToastProps> = ({
  message,
  type = ToastType.ERROR,
  duration = TOAST_DURATION,
  onClose,
  buttonProps = null,
}) => {
  const [isVisible, setIsVisible] = React.useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 500);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const toastIcon = getToastIcon(type);

  return isVisible ? (
    <ToastContainer type={type} $isVisible={isVisible} data-testid="toast">
      <ToastContent>
        {toastIcon && (
          <Image src={toastIcon} alt="icon" width={24} height={24} />
        )}
        <Text dangerouslySetInnerHTML={{ __html: message }} />
      </ToastContent>
      {buttonProps && (
        <Button onClick={buttonProps.onClick}>{buttonProps.label}</Button>
      )}
      <CloseButton
        type={type}
        onClick={onClose}
        data-testid="toast-close-button"
      >
        <Image src={CrossSVG} alt="cross" width={12} height={12} />
      </CloseButton>
    </ToastContainer>
  ) : null;
};

export default Toast;
